/**
 * wholesale-draft.js
 *
 * Handles fetching, displaying, and editing wholesale draft orders
 */

(function() {
  'use strict';

  // Configuration from Liquid
  const config = window.wholesaleDraftConfig || {};

  // State
  let currentDraft = null;

  // DOM Elements
  const elements = {
    loading: document.getElementById('draft-loading'),
    error: document.getElementById('draft-error'),
    errorMessage: document.getElementById('error-message'),
    noDraft: document.getElementById('no-draft'),
    content: document.getElementById('draft-content'),

    // Additional drafts
    additionalDraftsNotice: document.getElementById('additional-drafts-notice'),
    additionalDraftsCount: document.getElementById('additional-drafts-count'),
    additionalDraftsList: document.getElementById('additional-drafts-list'),
    pluralS: document.getElementById('plural-s'),

    // Header
    draftName: document.getElementById('draft-name'),
    draftDate: document.getElementById('draft-date'),
    draftStatus: document.querySelector('.draft-status strong'),

    // Note
    noteContainer: document.getElementById('draft-note-container'),
    note: document.getElementById('draft-note'),

    // Items
    itemsBody: document.getElementById('draft-items-body'),

    // Totals
    subtotal: document.getElementById('draft-subtotal'),
    total: document.getElementById('draft-total'),

    // Addresses
    shippingAddressBlock: document.getElementById('shipping-address-block'),
    shippingAddress: document.getElementById('shipping-address'),
    billingAddressBlock: document.getElementById('billing-address-block'),
    billingAddress: document.getElementById('billing-address'),

    // Actions
    editBtn: document.getElementById('edit-draft-btn'),
    invoiceLink: document.getElementById('view-invoice-link'),

    // Modal
    modal: document.getElementById('edit-confirmation'),
    confirmEditBtn: document.getElementById('confirm-edit-btn'),
    cancelEditBtn: document.getElementById('cancel-edit-btn')
  };

  /**
   * Initialize the draft viewer
   */
  function init() {
    if (!config.customerEmail || !config.customerId) {
      showError('You must be logged in to view draft orders.');
      return;
    }

    // Set up event listeners
    setupEventListeners();

    // Fetch the latest draft
    fetchLatestDraft();
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    if (elements.editBtn) {
      elements.editBtn.addEventListener('click', handleEditClick);
    }

    if (elements.confirmEditBtn) {
      elements.confirmEditBtn.addEventListener('click', handleConfirmEdit);
    }

    if (elements.cancelEditBtn) {
      elements.cancelEditBtn.addEventListener('click', hideModal);
    }

    // Close modal on background click
    if (elements.modal) {
      elements.modal.addEventListener('click', (e) => {
        if (e.target === elements.modal) {
          hideModal();
        }
      });
    }
  }

  /**
   * Fetch the latest draft order (or specific draft if draft_id in URL)
   */
  async function fetchLatestDraft() {
    try {
      showLoading();

      // The customer comes from the app proxy signature, not from these params
      const params = new URLSearchParams();

      // Check if a specific draft_id is requested in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const requestedDraftId = urlParams.get('draft_id');
      if (requestedDraftId) {
        params.append('draft_id', requestedDraftId);
      }

      const url = `${config.phpEndpoint}?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({ error: `The wholesale server didn't answer properly (${response.status}).` }));

      if (!response.ok) {
        if (response.status === 404) {
          showNoDraft();
        } else {
          throw new Error(data.message || data.error || 'Failed to fetch draft order');
        }
        return;
      }

      if (data.success && data.draft_order) {
        currentDraft = data;
        config.draftId = data.draft_order.id;
        renderDraft(data);
      } else {
        showNoDraft();
      }

    } catch (error) {
      console.error('Error fetching draft:', error);
      showError(error.message || 'Failed to load draft order');
    }
  }

  /**
   * Render the draft order
   */
  function renderDraft(data) {
    const { draft_order, cart_items, customer, shipping_address, billing_address, additional_drafts } = data;

    // Always render additional drafts section (handles "View Latest" link too)
    renderAdditionalDrafts(additional_drafts || []);

    // Header
    if (elements.draftName) {
      elements.draftName.textContent = draft_order.name;
    }

    if (elements.draftDate) {
      const date = new Date(draft_order.created_at);
      elements.draftDate.textContent = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // Note
    if (draft_order.note && draft_order.note.trim()) {
      elements.note.textContent = draft_order.note;
      elements.noteContainer.style.display = 'block';
    }

    // Line Items
    renderLineItems(cart_items);

    // Totals
    if (elements.subtotal) {
      elements.subtotal.textContent = formatMoney(parseFloat(draft_order.subtotal_price) * 100);
    }
    if (elements.total) {
      elements.total.textContent = formatMoney(parseFloat(draft_order.total_price) * 100);
    }

    // Addresses
    if (shipping_address) {
      renderAddress(shipping_address, elements.shippingAddress);
      elements.shippingAddressBlock.style.display = 'block';
    }

    if (billing_address) {
      renderAddress(billing_address, elements.billingAddress);
      elements.billingAddressBlock.style.display = 'block';
    }

    // Check if draft is tagged as "printed" and note status
    const isPrinted = String(draft_order.tags || '').toLowerCase().includes('printed');
    const draftStatus = (draft_order.status || '').toLowerCase();
    const isInvoiceSent = draftStatus === 'invoice_sent';
    const formattedStatus = formatStatusLabel(draft_order.status);

    if (elements.draftStatus) {
      elements.draftStatus.textContent = formattedStatus;
    }

    // Invoice link - only show if invoice has been sent
    if (draft_order.invoice_url && draft_order.invoice_sent_at && elements.invoiceLink) {
      elements.invoiceLink.href = draft_order.invoice_url;
      elements.invoiceLink.style.display = 'inline-block';
    }

    // Edit button - hide if order is printed
    if (elements.editBtn) {
      const parentNode = elements.editBtn.parentNode;

      if (parentNode) {
        // Remove any existing production notice before re-rendering
        const existingNotice = parentNode.querySelector('.draft-production-notice');
        if (existingNotice) {
          existingNotice.remove();
        }
      }

      const shouldHideEdit = isInvoiceSent || isPrinted;
      if (shouldHideEdit) {
        elements.editBtn.style.display = 'none';

        if (isPrinted && !isInvoiceSent && parentNode) {
          // Add "Already in Production" message only for printed (non-invoice sent) drafts
          const productionMsg = document.createElement('div');
          productionMsg.className = 'draft-production-notice';
          productionMsg.innerHTML = '<strong>Already In Production</strong><p>This order cannot be edited as it is currently being processed.</p>';
          parentNode.insertBefore(productionMsg, elements.editBtn);
        }
      } else {
        elements.editBtn.style.display = 'inline-flex';
      }
    }

    // Show content
    hideLoading();
    elements.content.style.display = 'block';
  }

  /**
   * Render additional drafts notice
   */
  function renderAdditionalDrafts(additionalDrafts) {
    if (!elements.additionalDraftsList) {
      return;
    }

    // Check if viewing a specific draft (not the latest)
    const urlParams = new URLSearchParams(window.location.search);
    const viewingSpecificDraft = urlParams.has('draft_id');

    // If no additional drafts and not viewing a specific draft, hide the notice
    if ((!additionalDrafts || additionalDrafts.length === 0) && !viewingSpecificDraft) {
      return;
    }

    // Update count
    if (elements.additionalDraftsCount) {
      elements.additionalDraftsCount.textContent = additionalDrafts.length;
    }

    // Handle plural
    if (elements.pluralS) {
      elements.pluralS.style.display = additionalDrafts.length === 1 ? 'none' : 'inline';
    }

    // Clear existing list
    elements.additionalDraftsList.innerHTML = '';

    // If viewing a specific draft, add a "View Latest" link at the top
    if (viewingSpecificDraft) {
      const latestLi = document.createElement('li');
      latestLi.style.borderBottom = '2px solid rgb(var(--color-border))';
      latestLi.style.paddingBottom = 'var(--sp-3)';
      latestLi.style.marginBottom = 'var(--sp-3)';

      const latestLink = document.createElement('a');
      latestLink.textContent = '← View Latest Draft Order';
      latestLink.className = 'draft-link latest-link';
      latestLink.style.fontWeight = 'var(--font-medium)';

      // Remove draft_id from URL to go back to latest
      const latestUrl = new URL(window.location.href);
      latestUrl.searchParams.delete('draft_id');
      latestLink.href = latestUrl.toString();

      latestLink.onclick = (e) => {
        e.preventDefault();
        window.location.href = latestUrl.toString();
      };

      latestLi.appendChild(latestLink);
      elements.additionalDraftsList.appendChild(latestLi);
    }

    // Add each additional draft as a link
    additionalDrafts.forEach(draft => {
      const li = document.createElement('li');
      const link = document.createElement('a');

      // Format the date
      const updatedDate = new Date(draft.updated_at);
      const formattedDate = updatedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      // Format the price
      const formattedPrice = formatMoney(parseFloat(draft.total_price) * 100);

      // Create link text
      link.textContent = `${draft.name} - ${formattedDate} - ${formattedPrice}`;
      link.className = 'draft-link';

      // Create URL with draft_id parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('draft_id', draft.id);
      link.href = currentUrl.toString();

      // Handle click to reload with the specific draft
      link.onclick = (e) => {
        e.preventDefault();
        // Update URL and reload
        window.location.href = currentUrl.toString();
      };

      li.appendChild(link);
      elements.additionalDraftsList.appendChild(li);
    });

    // Show the notice
    if (elements.additionalDraftsNotice) {
      elements.additionalDraftsNotice.style.display = 'block';
    }
  }

  /**
   * Render line items table
   */
  function renderLineItems(items) {
    if (!elements.itemsBody) return;

    elements.itemsBody.innerHTML = '';

    items.forEach(item => {
      const row = document.createElement('tr');

      const hasDiscount = item.original_price > item.final_price;

      row.innerHTML = `
        <td>
          <div class="product-title">${escapeHtml(item.product_title)}</div>
        </td>
        <td>
          <div class="variant-title">${escapeHtml(item.variant_title || '-')}</div>
        </td>
        <td>${escapeHtml(item.sku || '-')}</td>
        <td class="text-right">
          ${hasDiscount ? `<span class="price-original">${formatMoney(item.original_price)}</span>` : ''}
          <span class="${hasDiscount ? 'price-discounted' : ''}">${formatMoney(item.final_price)}</span>
        </td>
        <td class="text-center">${item.quantity}</td>
        <td class="text-right"><strong>${formatMoney(item.line_price)}</strong></td>
      `;

      elements.itemsBody.appendChild(row);
    });
  }

  function formatStatusLabel(status) {
    if (!status) return 'Open';
    const normalized = String(status).toLowerCase().replace(/_/g, ' ');
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  /**
   * Render an address
   */
  function renderAddress(address, container) {
    if (!container) return;

    const parts = [];

    if (address.firstName || address.lastName) {
      parts.push(`${address.firstName || ''} ${address.lastName || ''}`.trim());
    }
    if (address.address1) parts.push(address.address1);
    if (address.address2) parts.push(address.address2);

    const cityLine = [address.city, address.province, address.zip].filter(Boolean).join(', ');
    if (cityLine) parts.push(cityLine);

    if (address.country) parts.push(address.country);
    if (address.phone) parts.push(`Phone: ${address.phone}`);

    container.innerHTML = parts.map(p => `<p>${escapeHtml(p)}</p>`).join('');
  }

  /**
   * Handle edit button click
   */
  function handleEditClick() {
    if (elements.modal) {
      elements.modal.style.display = 'flex';
    }
  }

  /**
   * Handle confirm edit
   */
  async function handleConfirmEdit() {
    if (!currentDraft || !currentDraft.cart_items) {
      showError('No draft data available');
      return;
    }

    try {
      hideModal();
      elements.editBtn.disabled = true;
      elements.editBtn.textContent = 'Loading cart...';

      // Clear the cart first
      await clearCart();

      // Add all items to cart
      await addItemsToCart(currentDraft.cart_items, currentDraft.draft_order.id);

      // Store draft ID and shipping address in localStorage for later use
      localStorage.setItem('wholesale_original_draft_id', currentDraft.draft_order.id);

      // Store shipping address ID if available
      if (currentDraft.shipping_address && currentDraft.shipping_address.id) {
        localStorage.setItem('wholesale_shipping_address_id', currentDraft.shipping_address.id);
      }

      // Redirect to cart
      window.location.href = config.cartPageUrl || '/cart';

    } catch (error) {
      console.error('Error rehydrating cart:', error);
      showError('Failed to load items into cart: ' + error.message);
      elements.editBtn.disabled = false;
      elements.editBtn.textContent = 'Edit This Order';
    }
  }

  /**
   * Clear the current cart
   */
  async function clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }

    return response.json();
  }

  /**
   * Add items to cart
   */
  async function addItemsToCart(items, draftId) {
    // Build items array for cart
    // kit lines carry _kit_components so the kit comes back as the same kit
    const cartItems = items.map(item => ({
      id: item.id,
      quantity: item.quantity,
      ...(item.properties ? { properties: item.properties } : {})
    }));

    // Add items to cart using Shopify AJAX API
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: cartItems
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add items to cart');
    }

    // Update cart attributes with original draft ID
    await updateCartAttributes(draftId);

    return response.json();
  }

  /**
   * Update cart attributes
   */
  async function updateCartAttributes(draftId) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'original_draft_id': draftId,
          'is_draft_edit': 'true'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update cart attributes');
    }

    return response.json();
  }

  /**
   * Show/Hide states
   */
  function showLoading() {
    hideAllStates();
    if (elements.loading) elements.loading.style.display = 'block';
  }

  function hideLoading() {
    if (elements.loading) elements.loading.style.display = 'none';
  }

  function showError(message) {
    hideAllStates();
    if (elements.error) {
      elements.error.style.display = 'block';
      if (elements.errorMessage) {
        elements.errorMessage.textContent = message;
      }
    }
  }

  function showNoDraft() {
    hideAllStates();
    if (elements.noDraft) elements.noDraft.style.display = 'block';
  }

  function hideAllStates() {
    if (elements.loading) elements.loading.style.display = 'none';
    if (elements.error) elements.error.style.display = 'none';
    if (elements.noDraft) elements.noDraft.style.display = 'none';
    if (elements.content) elements.content.style.display = 'none';
  }

  function hideModal() {
    if (elements.modal) {
      elements.modal.style.display = 'none';
    }
  }

  /**
   * Utility functions
   */
  function formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  function escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text ? String(text).replace(/[&<>"']/g, m => map[m]) : '';
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
